import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';

type ToastType = 'success' | 'error' | 'info' | 'warning';

interface Toast {
  id: number;
  message: string;
  type: ToastType;
}

interface ToastContextValue {
  showToast: (message: string, type?: ToastType, duration?: number) => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

const TOAST_COLORS: Record<ToastType, { bg: string; border: string; text: string }> = {
  success: { bg: 'var(--success-bg)', border: 'var(--success)', text: 'var(--success-text)' },
  error:   { bg: 'var(--danger-bg)',  border: 'var(--danger)',  text: 'var(--danger-text)' },
  warning: { bg: 'var(--warning-bg)', border: 'var(--warning)', text: 'var(--warning-text)' },
  info:    { bg: 'var(--info-bg)',    border: 'var(--info)',    text: 'var(--info-text)' },
};

const ICONS: Record<ToastType, string> = {
  success: '\u2714',
  error:   '\u2718',
  warning: '\u26A0',
  info:    '\u2139',
};

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const idRef = useRef(0);
  const timersRef = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());
  const styleInjected = useRef(false);

  useEffect(() => {
    if (styleInjected.current) return;
    styleInjected.current = true;
    const style = document.createElement('style');
    style.textContent = `
      @keyframes toastSlideIn {
        from { transform: translateY(-20px); opacity: 0; }
        to   { transform: translateY(0);     opacity: 1; }
      }
      @keyframes toastFadeOut {
        from { opacity: 1; }
        to   { opacity: 0; transform: translateY(-10px); }
      }
    `;
    document.head.appendChild(style);
  }, []);

  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      timers.forEach(t => clearTimeout(t));
      timers.clear();
    };
  }, []);

  const showToast = useCallback((message: string, type: ToastType = 'info', duration = 3500) => {
    const id = ++idRef.current;
    setToasts(prev => [...prev, { id, message, type }]);
    const timer = setTimeout(() => {
      timersRef.current.delete(id);
      setToasts(prev => prev.filter(t => t.id !== id));
    }, duration);
    timersRef.current.set(id, timer);
  }, []);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <div style={{
        position: 'fixed',
        top: 24,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        maxWidth: 440,
        minWidth: 280,
        pointerEvents: 'none',
      }}>
        {toasts.map(t => {
          const c = TOAST_COLORS[t.type];
          return (
            <div key={t.id} style={{
              background: c.bg,
              border: `1px solid ${c.border}`,
              borderLeft: `4px solid ${c.border}`,
              color: c.text,
              padding: '12px 16px',
              borderRadius: 8,
              fontSize: '0.9rem',
              lineHeight: 1.4,
              boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
              animation: 'toastSlideIn 0.3s ease-out',
              display: 'flex',
              alignItems: 'flex-start',
              gap: 10,
              pointerEvents: 'auto',
            }}>
              <span style={{ fontSize: '1.1rem', flexShrink: 0, marginTop: 1 }}>
                {ICONS[t.type]}
              </span>
              <span>{t.message}</span>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = (): ToastContextValue => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
};
